/**
 * Fuse options for searching admin lists and pickers in the browser. Used by
 * useAdminSearch (tables) and fuseFilterOptions (pickers), so both rank the same way.
 */
export const SEARCH_DEFAULTS = {
	threshold: 0.2,
	// Match anywhere in a field, not just near the start
	ignoreLocation: true,
};

/**
 * Users: doc id, name, email, phone, delegated emails, and the names of their broker groups.
 * brokerGroupNames is a Map of broker group id → name (see useUserSearchOptions). On admin
 * user items `brokerGroup` is the primary broker group's id.
 */
export const getUserSearchOptions = (brokerGroupNames) => ({
	...SEARCH_DEFAULTS,
	keys: [
		'id',
		'name',
		'email',
		'displayEmail',
		'phoneNumber',
		'delegatedEmails',
		{
			name: 'brokerGroupNames',
			getFn: (user) =>
				[...new Set([user.brokerGroup, user.brokerGroupId, ...(user.brokerGroupArray ?? [])])]
					.map((id) => brokerGroupNames.get(id))
					.filter(Boolean),
		},
	],
});

/** Broker groups: doc id, name and ledger. */
export const BROKER_GROUP_SEARCH_OPTIONS = {
	...SEARCH_DEFAULTS,
	keys: ['id', 'name', 'ledger'],
};

/**
 * Feature flags: doc id, feature, description, ledgers, and the names / emails of the users and the
 * names of the broker groups the flag targets. Flag items hold user and broker group ids, so
 * usersById (id → user) and brokerGroupNames (id → name) are Maps from the full lists (see
 * useFeatureFlagSearchOptions).
 */
export const getFeatureFlagSearchOptions = ({ usersById, brokerGroupNames }) => ({
	...SEARCH_DEFAULTS,
	keys: [
		'id',
		'feature',
		'description',
		'ledgers',
		{
			name: 'userNames',
			getFn: (flag) =>
				(flag.users ?? [])
					.map((id) => usersById.get(id))
					.flatMap((user) => [user?.name, user?.email])
					.filter(Boolean),
		},
		{
			name: 'brokerGroupNames',
			getFn: (flag) =>
				(flag.brokerGroups ?? []).map((id) => brokerGroupNames.get(id)).filter(Boolean),
		},
	],
});

/** Ledger picker options are { id, name } where both are the ledger. */
export const LEDGER_SEARCH_OPTIONS = {
	...SEARCH_DEFAULTS,
	keys: ['name'],
};

/** Feature flag picker options are { id, name: feature, description }. */
export const FEATURE_FLAG_PICKER_SEARCH_OPTIONS = {
	...SEARCH_DEFAULTS,
	keys: ['name', 'description'],
};
